import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackEvent } from '../../analytics';
import { SERVICE_OUTCOMES } from '../../content/placeholders';
import CalEmbed from '../CalEmbed';
import { Sparkle } from '../brand/Jaipur';
import { HOME_SERVICES } from './ServicesPricing';
import { ArrowIcon, Eyebrow, WhatsAppIcon, focusRing, prefersReducedMotion, whatsappLink } from './shared';
import './home.css';

/**
 * Each option can add points to services; the highest total is the recommendation.
 * Keys: social-media, branding, design, web-development, marketing.
 */
const QUESTIONS = [
  {
    id: 'stage',
    question: 'Where’s your brand at right now?',
    options: [
      { label: 'Just an idea, no name yet', score: { branding: 3 } },
      { label: 'Launched, but still small', score: { 'social-media': 2, design: 1 } },
      { label: 'Growing, want it faster', score: { marketing: 2, 'social-media': 1 } },
      { label: 'Established, feeling a bit stale', score: { branding: 2, 'web-development': 1 } },
    ],
  },
  {
    id: 'broken',
    question: 'Be honest. What’s not working?',
    options: [
      { label: 'Nobody knows we exist', score: { 'social-media': 3 } },
      { label: 'Our brand looks dated', score: { branding: 3 } },
      { label: 'Posts and creatives look boring', score: { design: 3 } },
      { label: 'Website gets visits, no leads', score: { 'web-development': 3 } },
      { label: 'Ads burn money, no sales', score: { marketing: 3 } },
    ],
  },
  {
    id: 'goal',
    question: 'What would make the next few months a win?',
    options: [
      { label: 'More sales and enquiries', score: { marketing: 2 } },
      { label: 'A brand people remember', score: { branding: 2 } },
      { label: 'Content that shows up daily and performs', score: { 'social-media': 2, design: 1 } },
      { label: 'A site that actually sells', score: { 'web-development': 2 } },
    ],
  },
  {
    id: 'budget',
    question: 'How are you thinking about budget?',
    options: [
      { label: 'Starting small, testing the waters', score: { design: 1 } },
      { label: 'A steady monthly spend', score: { 'social-media': 1, marketing: 1 } },
      { label: 'Ready to go all in', score: { marketing: 1, branding: 1 } },
      { label: 'Not sure yet, guide me', score: {} },
    ],
  },
  {
    id: 'timeline',
    question: 'When do you want to start?',
    options: [
      { label: 'Kal se. As soon as possible', score: {} },
      { label: 'Sometime this month', score: {} },
      { label: 'In a couple of months', score: {} },
      { label: 'Just exploring for now', score: {} },
    ],
  },
  {
    id: 'vibe',
    question: 'Pick your brand’s vibe.',
    options: [
      { label: 'Bold and loud', score: { 'social-media': 1 } },
      { label: 'Clean and premium', score: { branding: 1, 'web-development': 1 } },
      { label: 'Desi and warm', score: { branding: 1, design: 1 } },
      { label: 'Playful and meme-y', score: { 'social-media': 1, design: 1 } },
    ],
  },
];

function recommend(answers) {
  const totals = {};
  QUESTIONS.forEach((q, qi) => {
    const opt = q.options[answers[qi]];
    if (!opt) return;
    Object.entries(opt.score).forEach(([slug, pts]) => {
      totals[slug] = (totals[slug] || 0) + pts;
    });
  });
  // Ties go to whatever they said is broken, then to service order.
  const brokenSlug = Object.keys(QUESTIONS[1].options[answers[1]]?.score || {})[0];
  const ranked = HOME_SERVICES.map((s) => s.slug).sort((a, b) => {
    const diff = (totals[b] || 0) - (totals[a] || 0);
    if (diff) return diff;
    if (a === brokenSlug) return -1;
    if (b === brokenSlug) return 1;
    return 0;
  });
  return HOME_SERVICES.find((s) => s.slug === ranked[0]) || HOME_SERVICES[0];
}

export default function VibeCheck() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [showCal, setShowCal] = useState(false);
  const headingRef = useRef(null);
  const interacted = useRef(false);
  const calRef = useRef(null);

  const done = step >= QUESTIONS.length;
  const service = done ? recommend(answers) : null;
  const outcome = service ? SERVICE_OUTCOMES[service.slug] : null;

  // Move focus to the new card for keyboard / screen reader users (not on first render).
  useEffect(() => {
    if (interacted.current && headingRef.current) headingRef.current.focus({ preventScroll: true });
  }, [step]);

  useEffect(() => {
    if (!done || !service) return;
    const payload = { recommended_service: service.slug };
    QUESTIONS.forEach((q, i) => {
      payload[q.id] = q.options[answers[i]]?.label || '';
    });
    trackEvent('vibe_check_complete', payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  useEffect(() => {
    if (showCal && calRef.current) {
      calRef.current.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
  }, [showCal]);

  const choose = (optionIndex) => {
    interacted.current = true;
    setAnswers((prev) => {
      const next = [...prev];
      next[step] = optionIndex;
      return next;
    });
    setStep((s) => s + 1);
  };

  const back = () => {
    interacted.current = true;
    setStep((s) => Math.max(0, s - 1));
  };

  const restart = () => {
    interacted.current = true;
    setAnswers([]);
    setShowCal(false);
    setStep(0);
  };

  const summary = done
    ? [
        `Hi Apnaaapan, I just did the vibe check on your site.`,
        ...QUESTIONS.map((q, i) => `- ${q.question} ${q.options[answers[i]]?.label || ''}`),
        `You suggested ${service.name}. Can we talk?`,
      ].join('\n')
    : '';

  const current = QUESTIONS[step];

  return (
    <section id="vibe-check" aria-labelledby="home-vibe-title" className="scroll-mt-24 overflow-x-clip bg-apn-surface-2 py-14 sm:py-20 short:py-10">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:px-8">
        <div>
          <Eyebrow>Brand vibe check</Eyebrow>
          <h2 id="home-vibe-title" data-animate="split-heading" className="mt-3 font-display text-4xl font-extrabold leading-[0.98] tracking-[-0.03em] text-apn-ink sm:text-5xl lg:text-6xl">
            Not sure what you need? Take the vibe check.
          </h2>
          <p className="mt-4 max-w-lg text-lg text-apn-text">
            Six quick taps. We’ll tell you where to start and what it costs, then you can WhatsApp us or grab a call slot.
          </p>
        </div>

        <div className="relative">
          {/* Stacked-card look behind the active card */}
          <div aria-hidden="true" className="absolute inset-0 translate-x-3 translate-y-3 rotate-2 rounded-[28px] bg-apn-pink" />
          <div aria-hidden="true" className="absolute inset-0 translate-x-1.5 translate-y-1.5 rotate-1 rounded-[28px] bg-apn-saffron" />

          <div className="relative min-h-[30rem] rounded-[28px] border border-apn-line bg-apn-surface p-5 sm:p-8">
            <div className="flex items-center gap-1.5" aria-hidden="true">
              {QUESTIONS.map((q, i) => (
                <span key={q.id} className={`h-1.5 flex-1 rounded-full ${i < step || done ? 'bg-apn-orange' : i === step ? 'bg-apn-ink' : 'bg-apn-line'}`} />
              ))}
            </div>

            {!done ? (
              <div key={step} className="home-quiz-card mt-6">
                <p className="text-sm font-semibold text-apn-muted">
                  {step + 1} of {QUESTIONS.length}
                </p>
                <h3
                  ref={headingRef}
                  tabIndex={-1}
                  className="mt-1 font-display text-2xl font-extrabold leading-tight tracking-[-0.02em] text-apn-ink outline-none sm:text-3xl"
                >
                  {current.question}
                </h3>
                <ul className="mt-5 grid gap-2.5">
                  {current.options.map((opt, oi) => {
                    const selected = answers[step] === oi;
                    return (
                      <li key={opt.label}>
                        <button
                          type="button"
                          onClick={() => choose(oi)}
                          aria-pressed={selected}
                          className={`flex min-h-[52px] w-full items-center justify-between gap-3 rounded-2xl border-2 px-4 py-3 text-left text-base font-semibold transition ${
                            selected
                              ? 'border-apn-orange bg-apn-orange text-white'
                              : 'border-apn-line bg-apn-bg text-apn-ink hover:border-apn-ink'
                          } ${focusRing}`}
                        >
                          {opt.label}
                          <ArrowIcon className="h-4 w-4 shrink-0 opacity-60" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
                {step > 0 ? (
                  <button type="button" onClick={back} className={`mt-4 inline-flex min-h-[44px] items-center gap-2 rounded-full px-2 text-sm font-semibold text-apn-muted hover:text-apn-ink ${focusRing}`}>
                    <ArrowIcon direction="left" className="h-4 w-4" />
                    Back
                  </button>
                ) : null}
              </div>
            ) : (
              <div key="result" className="home-quiz-card mt-6" aria-live="polite">
                <p className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.16em] text-apn-orange">
                  <Sparkle className="h-4 w-4 text-apn-pink" />
                  Your starting point
                </p>
                <h3 ref={headingRef} tabIndex={-1} className="mt-2 font-display text-4xl font-extrabold leading-none tracking-[-0.03em] text-apn-ink outline-none sm:text-5xl">
                  {service.name}
                </h3>
                {outcome ? <p className="mt-3 text-lg text-apn-text">{outcome}.</p> : null}

                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                  <a
                    href={whatsappLink(summary)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-cta="home-vibe-whatsapp"
                    className={`inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full bg-apn-ink px-6 font-semibold text-apn-bg transition hover:bg-apn-orange hover:text-white ${focusRing}`}
                  >
                    <WhatsAppIcon />
                    Send my answers
                  </a>
                  <button
                    type="button"
                    onClick={() => setShowCal(true)}
                    data-cta="home-vibe-book-call"
                    aria-expanded={showCal}
                    aria-controls="home-vibe-calendar"
                    className={`inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full border-2 border-apn-ink px-6 font-semibold text-apn-ink transition hover:border-apn-orange hover:text-apn-orange ${focusRing}`}
                  >
                    Book a call
                  </button>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1">
                  <Link to={`/services/${service.slug}`} data-cta="home-vibe-service" className={`inline-flex min-h-[44px] items-center gap-1.5 rounded text-sm font-semibold text-apn-ink underline decoration-apn-orange decoration-2 underline-offset-4 ${focusRing}`}>
                    What’s included
                  </Link>
                  <button type="button" onClick={restart} className={`inline-flex min-h-[44px] items-center rounded text-sm font-semibold text-apn-muted hover:text-apn-ink ${focusRing}`}>
                    Retake the vibe check
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showCal ? (
        <div id="home-vibe-calendar" ref={calRef} className="mx-auto mt-12 max-w-6xl scroll-mt-24 px-4 sm:px-6 lg:px-8">
          <h3 className="mb-4 font-display text-2xl font-extrabold text-apn-ink sm:text-3xl">Pick a slot. 30 minutes, free.</h3>
          <CalEmbed
            namespace="home"
            location="home-vibe-check"
            className="h-[1000px] w-full overflow-hidden rounded-2xl bg-apn-surface sm:h-[700px] md:h-[760px]"
          />
        </div>
      ) : null}
    </section>
  );
}
